"use client"

import { TrackedLeadLink } from "./tracked-lead-link"
import { WhatsAppIcon } from "./icons"
import { useCtaVisibility } from "./use-cta-visibility"

/**
 * Floating WhatsApp button, from the `sm` breakpoint up.
 *
 * Deliberately not shown on phones: `MobileCtaBar` already sits there, and a
 * full width labelled bar beats a circle on a phone. It is a bigger target, it
 * says what it does, and it does not cover content the way a circle parked over
 * the text does. Two persistent WhatsApp CTAs at once would just compete.
 *
 * Ink, not WhatsApp green, for the same reason as every other button here. The
 * glyph carries the channel. Green would also fail contrast: white on #25D366
 * is about 2,1:1 and paper on it 1,74:1, both under the 3:1 that a meaningful
 * icon needs. Switching to green is a one line change if the owner prefers the
 * conventional look, but it costs legibility and it is the one element that
 * would make this page look like every other clinic site.
 */
export function FloatingWhatsApp({
  whatsappUrl,
  label = "Falar no WhatsApp",
}: {
  whatsappUrl: string
  label?: string
}) {
  const visible = useCtaVisibility()

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 hidden sm:block ${
        visible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
      } transition-opacity duration-200 motion-reduce:transition-none`}
      // Hidden from assistive tech while it is invisible, so a keyboard user
      // never tabs into a button that is not on screen.
      aria-hidden={!visible}
    >
      <TrackedLeadLink
        href={whatsappUrl}
        cta="floating_whatsapp"
        leadType="whatsapp"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        title={label}
        tabIndex={visible ? undefined : -1}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-paper shadow-[0_2px_12px_rgba(31,24,19,0.28)] transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink motion-reduce:transition-none"
      >
        <WhatsAppIcon className="h-6 w-6 shrink-0" />
      </TrackedLeadLink>
    </div>
  )
}
